import { formatDay, formatTime, formatWeekdayTime, tt } from '@agos/shared'

/**
 * How long a ticket has been sitting, and how long until a pre-order is due.
 *
 * The colour is the whole point of the kitchen display: a cook glances across
 * the room and reads red before they read anything else. The four bands are
 * ported unchanged from legacy `pages/kitchen.tsx`.
 *
 * Every function here takes `now` rather than calling `Date.now()`, so the
 * screen measures against the moment the list arrived rather than against the
 * clock — FB-039, found on the pre-order board and the same bug here.
 */

const MINUTE_MS = 60_000

export type ElapsedBand = 'fresh' | 'warming' | 'late' | 'overdue'

const BAND_MINUTES: Array<[ElapsedBand, number]> = [
  ['fresh', 5],
  ['warming', 10],
  ['late', 15],
]

export const ELAPSED_TEXT_COLORS: Record<ElapsedBand, string> = {
  fresh: 'text-green-700 dark:text-green-400',
  warming: 'text-yellow-700 dark:text-yellow-400',
  late: 'text-orange-700 dark:text-orange-400',
  overdue: 'text-red-700 dark:text-red-400',
}

export const ELAPSED_BG_COLORS: Record<ElapsedBand, string> = {
  fresh: 'bg-green-500/10',
  warming: 'bg-yellow-500/10',
  late: 'bg-orange-500/10',
  overdue: 'bg-red-500/10',
}

export function elapsedMinutes(since: string, now: number): number {
  return Math.floor((now - new Date(since).getTime()) / MINUTE_MS)
}

export function elapsedBand(since: string, now: number): ElapsedBand {
  const minutes = elapsedMinutes(since, now)
  for (const [band, limit] of BAND_MINUTES) {
    if (minutes < limit) return band
  }
  return 'overdue'
}

/** `< 1 min`, `7 min`, `1h 20m`. */
export function elapsedFormatted(since: string, now: number): string {
  const minutes = elapsedMinutes(since, now)
  if (minutes < 1) return '< 1 min'
  if (minutes < 60) return `${minutes} min`
  return `${Math.floor(minutes / 60)}h ${minutes % 60}m`
}

export function minutesUntil(target: string, now: number): number {
  return Math.floor((new Date(target).getTime() - now) / MINUTE_MS)
}

/** `Teraz!`, `25 min`, `2h 5m`. */
export function formatCountdown(minutes: number): string {
  if (minutes <= 0) return tt('Teraz!')
  if (minutes < 60) return `${minutes} min`
  return `${Math.floor(minutes / 60)}h ${minutes % 60}m`
}

/**
 * Whether a pre-order is close enough to be cooked now.
 *
 * **Moved to `@agos/shared` and re-exported - §9.643, owner O34.** The dispatch board needed the same rule
 * and `manager-delivery` does not depend on this module, so the definition lives in the one package both
 * reach. Re-exported rather than removed, because `KitchenPage` and its tests name it here and a rename
 * across them would be churn with no behaviour in it.
 */
export { isPreorderDueNow } from '@agos/shared'

/**
 * When a queued pre-order is due, for the §9.318 section - the owner's Q8.
 *
 * **Today's says the time, a later day says the weekday and the date**, because *"12:00"* on a card
 * with no day is the one thing that could make a cook start tomorrow's order tonight. Legacy makes
 * the same distinction on its dispatch card (`dispatch.tsx:643`) and its kitchen queue.
 *
 * **Formatted through `@agos/shared`, never `toLocaleTimeString`.** `eslint` refused the first
 * version under FRONTEND-BUGS N-01 and was right: those follow the **device's** zone, so a tablet
 * set to another one would put a Bratislava evening on the wrong day - which is the whole failure
 * this card exists to prevent. `formatWeekdayTime` was already there, documented *"for the pre-order
 * board"*.
 *
 * The countdown comes with it while it is under two hours, which is when it starts mattering.
 */
export function preorderWhen(scheduledAt: string | null | undefined, now: number): string {
  if (!scheduledAt) return ''
  const when = new Date(scheduledAt)
  if (Number.isNaN(when.getTime())) return ''

  const minutes = minutesUntil(scheduledAt, now)
  const countdown = minutes > 0 && minutes < 120 ? ` · ${formatCountdown(minutes)}` : ''
  // The same day in the **restaurant's** zone, which is what `formatDay` answers for both.
  const sameDay = formatDay(when) === formatDay(new Date(now))
  return sameDay
    ? `${tt('Dnes')} ${formatTime(when)}${countdown}`
    : `${formatWeekdayTime(when)}${countdown}`
}

export interface SortableTicket {
  status?: string
  type?: string
  createdAt?: string
  /** When a časenka is due - §9.471. Absent on an ordinary order, which is most of them. */
  scheduledAt?: string | null
}

/**
 * When this ticket is due, or `null` if it is not a časenka - §9.471.
 *
 * **The predicate is *parses to a finite time*, not *the field is present*.** `new Date(undefined)` is
 * an Invalid Date and `NaN - NaN` is `NaN`; a comparator that returns `NaN` makes
 * `Array.prototype.sort` behave arbitrarily, so the board would reshuffle itself on every eight-second
 * poll. A malformed timestamp is treated as no časenka rather than as one due at an unknown moment.
 */
function dueAt(ticket: SortableTicket): number | null {
  if (ticket.scheduledAt === undefined || ticket.scheduledAt === null) return null
  const when = new Date(ticket.scheduledAt).getTime()
  return Number.isFinite(when) ? when : null
}

/**
 * The order the cards are laid out in: a časenka first and by when it is due, then what is already
 * being made, then the restaurant's own tables, then oldest first.
 *
 * Restaurant before delivery is not arbitrary - a seated guest is waiting at a
 * table in the room, and a delivery has a driver's travel time absorbing a few
 * minutes of slack.
 *
 * **§9.471, the owner:** *"In Kuchyňa when there is časenka, maake it first in the order and also order
 * by date, so if there is oone 38 minutes that need to be done and one in 58 minutes, make sure that 38
 * minutes will be first"*. Before this the three rules below were the whole comparator - byte-for-byte
 * legacy's `kitchen.tsx:584` - and `scheduledAt` was not a field of `SortableTicket` at all, so two
 * pre-orders sorted by **when they were placed**: one taken at 11:00 for 13:00 sat above one taken at
 * 11:30 for 12:00.
 *
 * **§9.574, the owner, 2026-09-28:** *"in kitchen when you click on «PRIJATÁ» and will be changed so we
 * are working on it (will show button «HOTOVÁ») make sure it will be first in the kitchen list,
 * reodered"*. **So the status key is now above the časenka key, which is the swap the paragraph below
 * had already costed.** It read:
 *
 * > The časenka key is deliberately above the status key [...] The cost is that a cook part-way through
 * > an ordinary dish sees that card move below a časenka entering the window; **swapping the two blocks
 * > is the one-line change if the owner would rather keep what is on the stove at the top.**
 *
 * **§9.471 survives it, and that is checked rather than assumed.** That request was an ordering *among*
 * časenky - *"if there is one 38 minutes [...] and one in 58 minutes, make sure that 38 minutes will be
 * first"* - and the due key still ranks inside each status group, so 38 still precedes 58 among the
 * accepted cards and again among the untouched ones. What changed is only that a card somebody is
 * cooking is no longer pushed below a časenka nobody has started.
 */
/**
 * Whether the customer for this ticket is in the building - §9.475.
 *
 * <p><b>`onPremise`, which is the reason the comparator's own javadoc already gives</b>: the axis is
 * *is the customer here now, with no travel time absorbing the wait*. It covers `restaurant`, `bar`, a
 * future *Terasa* and *na sekeru*. `usesTables` would also have fixed `bar` - it has both flags - and got
 * the reason wrong, which is how the next channel gets misfiled.
 *
 * <p><b>The fallback keeps today's answer.</b> A kitchen screen whose channels have not arrived must not
 * reshuffle the board, so an unresolved channel sorts by the one name this rule has always read.
 */
function isInTheRoom(type: string, channels?: readonly OrderingChannel[]): boolean {
  const known = channels?.find((channel) => channel.slug === type)
  if (known?.onPremise !== undefined) return known.onPremise
  return type === 'restaurant'
}

/**
 * The franchise's own precedence for a channel, or null when it cannot be told - §9.574.
 *
 * <p><b>The owner:</b> *"So make sure its like this: Reštaurácia -> Osobný Odber -> Rozvoz"*. That is
 * `sort_order` on their own `sales_channels` rows, settable at *Nastavenia -> Predajné kanály ->
 * Poradie*, and `FilterChannel` already carries it - so the board follows the number the owner can
 * change rather than a list in this file.
 *
 * <p><b>Null rather than a large number for an unknown channel.</b> Sorting an unrecognised slug to the
 * end would reorder every card the moment the channels read fails or is still in flight; the caller
 * skips the key instead, which is the same reasoning {@link isInTheRoom}'s fallback carries.
 */
function channelRank(type: string, channels?: readonly OrderingChannel[]): number | null {
  const known = channels?.find((channel) => channel.slug === type)
  return typeof known?.sortOrder === 'number' ? known.sortOrder : null
}

/** Only what the ordering needs - so any channel-shaped list can be passed. */
export interface OrderingChannel {
  slug?: string
  onPremise?: boolean
  /** §9.574 - the franchise's own display order, which is what the owner asked the board to follow. */
  sortOrder?: number
}

/**
 * The board's order for a franchise whose channels are known - §9.475.
 *
 * <p><b>A factory rather than an optional third parameter on the comparator.</b>
 * `Array.prototype.sort` calls a comparator with two arguments and would never pass a third, so an
 * optional parameter there could only ever be undefined - `agos-a-gate-that-can-never-open` in miniature.
 */
export function kitchenTicketOrder(
  channels?: readonly OrderingChannel[],
): (left: SortableTicket, right: SortableTicket) => number {
  return (left, right) => compareTickets(left, right, channels)
}

/** The no-channel form, kept exported so every existing caller and test reads the same behaviour. */
export function compareKitchenTickets(left: SortableTicket, right: SortableTicket): number {
  return compareTickets(left, right, undefined)
}

function compareTickets(
  left: SortableTicket,
  right: SortableTicket,
  channels?: readonly OrderingChannel[],
): number {
  // §9.574 - **what is on the stove, above everything else.** This key was below the časenka key until
  // the owner asked for the card they have just accepted to go to the top.
  const byStatus = (ticket: SortableTicket) => (ticket.status === 'preparing' ? 0 : 1)
  if (byStatus(left) !== byStatus(right)) return byStatus(left) - byStatus(right)

  // §9.471 - and inside each status group the časenka still sorts by when it is due, which is what that
  // request was actually about: 38 minutes before 58 minutes.
  const leftDue = dueAt(left)
  const rightDue = dueAt(right)

  if (leftDue !== null && rightDue !== null && leftDue !== rightDue) return leftDue - rightDue
  if (leftDue !== null && rightDue === null) return -1
  if (leftDue === null && rightDue !== null) return 1

  // §9.475 - the capability, so `bar` stops sorting with the deliveries. It always did, and the
  // franchise has had a bar channel all along.
  const byType = (ticket: SortableTicket) => (isInTheRoom(ticket.type ?? '', channels) ? 0 : 1)
  if (byType(left) !== byType(right)) return byType(left) - byType(right)

  /* §9.574 - **the franchise's own order, within the group rather than across it.** The owner asked for
   * *Reštaurácia -> Osobný Odber -> Rozvoz*, and on their rows that is exactly what this produces with no
   * data change: `restaurant` is 20 and `bar` - which they renamed *Osobný Odber* - is 30, both in the
   * room, so Rozvoz stays last by the key above.
   *
   * **Above `createdAt` and below `isInTheRoom`, and inverting the second would be a regression**:
   * Nesvady's `delivery` is `sort_order` **10**, the lowest of the three, so ranking by it alone would put
   * Rozvoz first until somebody edited three rows. A fix whose default is worse until the data moves is
   * half a fix.
   *
   * **Either side unknown skips the key**, so a board whose channels have not arrived keeps the order it
   * has rather than reshuffling on a failed read. */
  const leftRank = channelRank(left.type ?? '', channels)
  const rightRank = channelRank(right.type ?? '', channels)
  if (leftRank !== null && rightRank !== null && leftRank !== rightRank) return leftRank - rightRank

  return new Date(left.createdAt ?? 0).getTime() - new Date(right.createdAt ?? 0).getTime()
}

export type ItemProgress = 'idle' | 'working' | 'done'

/**
 * Tapping a line cycles it: untouched → being made → made → untouched.
 *
 * Stored on the line since §9.811 (BACKEND.md §10A.454, owner O157: *"vykliknutie nech sa zobrazuje aj na
 * ostatných zariadeniach a nech po refreshi zostane vykliknuté"*). Legacy kept it on the one tablet and so did this
 * port, until the owner asked for every tablet to see it and a refresh to keep it.
 */
export function nextItemProgress(current: ItemProgress): ItemProgress {
  if (current === 'done') return 'idle'
  if (current === 'working') return 'done'
  return 'working'
}
